//UploadVideoAndSelectThumbnail
import React, { useState, useRef, useEffect } from "react";
import {
  FormControl,
  FormLabel,
  VStack,
  Box,
  Button,
  Input,
  Text,
  Spinner,
  Flex,
  Image,
} from "@chakra-ui/react";
import { FiUpload, FiX } from "react-icons/fi";
import { useToast } from "@chakra-ui/react";
import axios from "axios";

interface BannerImageUploaderProps {
  label: string;
  fieldName: string;
  currentImage: string | string[] | any;
  setFieldValue: (field: string, value: any) => void;
  isVideo?: boolean;
  enforceImageDimensions?: boolean;
  isWatermark?: boolean;
  allowBothTypes?: boolean;
  imageLabelName?: string | any; // Optional, used for thumbnail upload
  imageFieldName?: string | any; // Optional, used for thumbnail upload
  currentImageForThumbnail?: string | any; // Optional, used to check if thumbnail is already set
  videoError: string | any; // Optional, used to display video upload errors
  thumbnailError?: string | any; // Optional, used to display thumbnail upload errors
  bannerData?: any; // Optional, used to pass additional data
}

export const UploadVideoAndSelectThumbnail: React.FC<
  BannerImageUploaderProps
> = ({
  label,
  fieldName,
  currentImage,
  setFieldValue,
  isVideo = false,
  enforceImageDimensions = false,
  isWatermark = true,
  allowBothTypes = false,
  imageFieldName,
  imageLabelName,
  currentImageForThumbnail,
  videoError,
  thumbnailError,
  bannerData,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [loadingThumbs, setLoadingThumbs] = useState(false);
  const [imageUploaded, setImageUploaded] = useState(false);
  const [selectedThumb, setSelectedThumb] = useState<string | null>(null);
  // const [isDisabledButton, setIsDisabledButton] = useState(false);
  const [mediaType, setMediaType] = useState<"image" | "video">(
    isVideo ? "video" : "image"
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const toast = useToast();

  useEffect(() => {
    if (currentImage) {
      const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi"];
      const isVideoFile = videoExtensions.some((ext) => {
        if (typeof currentImage === "string") {
          return currentImage.toLowerCase().includes(ext);
        } else {
          return currentImage.url.toLowerCase().includes(ext);
        }
      });
      setMediaType(isVideoFile ? "video" : "image");
    }
  }, [currentImage]);

  useEffect(() => {
    if (currentImageForThumbnail) {
      setSelectedThumb(
        `${process.env.REACT_APP_IMAGE_BASE_URL}/${currentImageForThumbnail}`
      );
    }
  }, []);

  // upload handler
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    setIsUploading(true);
    try {
      setUploadedFile(file);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("isWatermark", String(isWatermark));
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/image`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const url = res.data[0].imageURL;
      setFieldValue(fieldName, url);
      toast({
        title: "Upload successful",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Upload failed",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  // generate thumbnails every 0.5s
  const generateThumbnails = async () => {
    // Always enable the CORS mode on the video tag
    setLoadingThumbs(true);
    setImageUploaded(false);
    setSelectedThumb(null);
    if (videoRef.current) {
      videoRef.current.crossOrigin = "anonymous";
    }

    // Decide whether we have a newly uploaded File or need to fetch the existing URL
    let objectUrl: string | null = null;
    try {
      if (uploadedFile) {
        objectUrl = URL.createObjectURL(uploadedFile);
      } else if (currentImage) {
        const videoPath =
          typeof currentImage === "string" ? currentImage : currentImage.url;
        if (!videoPath) {
          return;
        }
        const fullUrl = `${process.env.REACT_APP_IMAGE_BASE_URL}/${videoPath}`;
        const response = await fetch(fullUrl, { mode: "cors" });
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
      } else {
        return;
      }

      setLoadingThumbs(true);
      setThumbnails([]);
      setSelectedThumb(null);

      const videoEl = videoRef.current!;
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d")!;
      const thumbs: string[] = [];

      videoEl.src = objectUrl;
      videoEl.load();
      videoEl.onloadedmetadata = () => {
        const duration = videoEl.duration;
        const step = 0.5; // capture a frame every 0.5 seconds
        let currentTime = 0;

        const capture = () => {
          videoEl.currentTime = currentTime;
        };

        videoEl.onseeked = () => {
          canvas.width = videoEl.videoWidth;
          canvas.height = videoEl.videoHeight;
          ctx.drawImage(videoEl, 0, 0);
          thumbs.push(canvas.toDataURL()); // convert frame to data URL
          currentTime += step;
          if (currentTime <= duration) {
            capture();
          } else {
            setThumbnails(thumbs);
            setSelectedThumb(thumbs[0] || null);
            setLoadingThumbs(false);
            if (objectUrl) URL.revokeObjectURL(objectUrl);
          }
        };

        capture();
      };
    } catch (err) {
      console.error("Thumbnail generation failed:", err);
      setLoadingThumbs(false);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  };

  const removeMedia = () => {
    setFieldValue(fieldName, "");
    setThumbnails([]);
    setSelectedThumb(null);
    setUploadedFile(null);
    setFieldValue("banner_thumbUrl", "");
  };

  const selectThumbnail = (src: string) => {
    setSelectedThumb(src);
    setImageUploaded(false);
    setFieldValue("banner_thumbUrl", src);
  };

  const handleUploadThumbnail = async (src: string) => {
    if (!src) return;
    setIsUploading(true);
    try {
      const blob = await fetch(src).then((res) => res.blob());
      const urlPath = currentImage;
      const filenameWithExt = urlPath.split("/").pop()!;
      const lastDot = filenameWithExt.lastIndexOf(".");
      const baseName =
        lastDot > 0 ? filenameWithExt.slice(0, lastDot) : filenameWithExt;

      const extension = lastDot > 0 ? filenameWithExt.slice(lastDot + 1) : "";

      const mimeType = blob.type || `video/${extension}`;
      const fileName = extension ? `${baseName}.png` : baseName;
      console.log("Uploading thumbnail:", fileName);
      const file = new File([blob], fileName, { type: mimeType });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("isWatermark", String(isWatermark));
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/image/uploadImage`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      console.log("Thumbnail upload response:", res.data);
      


      if (fieldName === "banner_videoURL") {
        setFieldValue("banner_thumbUrl", res.data);
      } else {
        setFieldValue(imageFieldName,  res.data);
      }
      setImageUploaded(true);
      toast({
        title: "Thumbnail uploaded successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      // setIsDisabledButton(true)
    } catch {
      toast({
        title: "Thumbnail upload failed",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      // setIsDisabledButton(false)
    } finally {
      setIsUploading(false);
    }
  };

  console.log("bannerData", bannerData);

  const isCurrentVideo = allowBothTypes ? mediaType === "video" : isVideo;
  return (
    <FormControl mb={4}>
      <FormLabel>{label}</FormLabel>
      <VStack spacing={4} align="stretch">
        {/* Upload button */}
        {currentImage && (
          <Box position="relative" width="100%" maxWidth="300px">
            <Button
              size="sm"
              position="absolute"
              top={2}
              right={2}
              colorScheme="red"
              onClick={removeMedia}
              borderRadius="full"
              zIndex={1}
              p={0}
              minW="30px"
              height="30px"
            >
              <FiX />
            </Button>
            {isCurrentVideo ? (
              <video
                src={`${process.env.REACT_APP_IMAGE_BASE_URL}/${currentImage}`}
                controls
                style={{ maxHeight: "200px", width: "100%" }}
              />
            ) : (
              <Image
                src={`${process.env.REACT_APP_IMAGE_BASE_URL}/${currentImage}`}
                alt={label}
                borderRadius="md"
                maxH="200px"
                objectFit="contain"
                width="100%"
                border="1px solid"
                borderColor="gray.200"
              />
            )}
          </Box>
        )}
        {!currentImage && (
          <Button
            as="label"
            htmlFor={`media-upload-${fieldName}`}
            leftIcon={<FiUpload />}
            variant="outline"
            isLoading={isUploading}
            height="100px"
            colorScheme={
              typeof videoError === "string" && videoError ? "red" : "blue"
            }
            cursor="pointer"
            border={
              typeof videoError === "string" && videoError
                ? "3px dashed red"
                : "1px dashed "
            }
          >
            <Text
              color={
                typeof videoError === "string" && videoError
                  ? "red.500"
                  : "blue.500"
              }
            >{`Upload ${isVideo ? "Video" : "Image"}`}</Text>
            <Input
              id={`media-upload-${fieldName}`}
              type="file"
              accept={isVideo ? "video/*" : "image/*"}
              onChange={handleUpload}
              display="none"
            />
          </Button>
        )}

        {/* Get Thumbnails button */}
        {(uploadedFile || currentImage) && isVideo && !isUploading && (
          <Button
            w={"300px"}
            onClick={generateThumbnails}
            variant={"outline"}
            colorScheme="blue"
            disabled={loadingThumbs}
          >
            Get Thumbnails
          </Button>
        )}

        {/* Loading indicator */}
        {loadingThumbs && <Spinner mt={4} />}

        {/* Preview of selected thumbnail */}
        {selectedThumb && (
          <Box position="relative" width="100%" maxWidth="100%">
            <Image
              src={selectedThumb}
              alt={label}
              borderRadius="md"
              maxH="200px"
              objectFit="contain"
              width="200px"
              border="1px solid"
              borderColor="gray.200"
            />{" "}
            <Button
              as="label"
              // htmlFor={`media-upload-${fieldName}`}
              leftIcon={<FiUpload />}
              variant="outline"
              isLoading={isUploading}
              // borderStyle="dashed"
              cursor={"pointer"}
              colorScheme="blue"
              w={"300px"}
              mt={4}
              disabled={isUploading}
              onClick={() => handleUploadThumbnail(selectedThumb)}
            >
              <Text mt={2}>
                {(bannerData && selectedThumb && imageUploaded) ||
                (bannerData && imageUploaded)
                  ? "Uploaded"
                  : "Upload Image"}
              </Text>
            </Button>
          </Box>
        )}

        {/* Scrollable thumbnails bar */}
        {thumbnails.length > 0 && (
          <Flex overflowX="auto" py={1}>
            {thumbnails.map((src, idx) => (
              <Box
                key={idx}
                minW="100px"
                minH="56px"
                mr={2}
                border={selectedThumb === src ? "2px solid" : "1px solid"}
                borderColor={selectedThumb === src ? "blue.500" : "gray.200"}
                cursor="pointer"
                onClick={() => selectThumbnail(src)}
              >
                <Image
                  src={src}
                  alt={`thumb-${idx}`}
                  objectFit="cover"
                  width="100%"
                  height="56px"
                />
              </Box>
            ))}
          </Flex>
        )}

        {/* Hidden video & canvas for capture */}
        <Box display="none">
          <video ref={videoRef} />
          <canvas ref={canvasRef} />
        </Box>
      </VStack>
    </FormControl>
  );
};
